import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { services, UNIVERSAL_OFFERINGS } from "@/lib/services-data";
import { getServiceConversion } from "@/lib/service-conversion";
import { INTENT_PAGES } from "@/lib/intent-pages";

import { Disclaimer } from "@/components/site/Disclaimer";
import { RelatedServices } from "@/components/site/RelatedServices";

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "Services | Nkadimeng Danny Legal & Compliance Support" },
      {
        name: "description",
        content:
          "Labour relations, CCMA support, legal drafting, compliance advisory, debt assistance and Court Online support across South Africa.",
      },
      { property: "og:title", content: "Our Legal & Compliance Services" },
      {
        property: "og:description",
        content:
          "Professional legal support tailored to individuals, employees, employers and businesses.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://nkadimengdannylcs.com/services" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/services" }],
  }),
  component: ServicesPage,
});

function ServicesPage() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-24">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            Our services
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Practical legal and compliance support across the matters that
            matter most — for individuals, employees, employers and growing
            businesses.
          </p>
          <div className="mt-6 max-w-3xl rounded-lg border border-gold/40 bg-gold/5 p-4">
            <p className="text-sm font-medium text-navy">
              Included across every service
            </p>
            <ul className="mt-2 space-y-2">
              {UNIVERSAL_OFFERINGS.map((o) => (
                <li key={o} className="flex items-start gap-2 text-sm text-foreground">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-6 max-w-3xl">
            <Disclaimer variant="compact" />
          </div>
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => (
            <Link
              key={s.slug}
              to="/services/$slug"
              params={{ slug: s.slug }}
              className="group rounded-xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-0.5 hover:shadow-lifted"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-gold/15 text-navy">
                <s.icon className="h-5 w-5" />
              </div>
              <h2 className="mt-4 font-display text-xl text-navy">
                {s.title}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {s.short}
              </p>
              <dl className="mt-4 space-y-1 border-t border-border pt-3 text-xs">
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 font-medium text-navy">Turnaround</dt>
                  <dd className="text-muted-foreground">
                    {getServiceConversion(s.slug).turnaround}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 font-medium text-navy">Fees</dt>
                  <dd className="text-muted-foreground">
                    {getServiceConversion(s.slug).pricingHint}
                  </dd>
                </div>
              </dl>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-navy group-hover:text-gold">
                View deliverables <ArrowRight className="h-3.5 w-3.5" />
              </span>

            </Link>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="container-narrow py-12">
          <h2 className="font-display text-2xl text-navy">Popular requests</h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Specific help people ask us for most often, with deliverables,
            turnaround and fee guidance on each page.
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {INTENT_PAGES.map((p) => (
              <Link
                key={p.slug}
                to="/legal-help/$topic"
                params={{ topic: p.slug }}
                className="rounded-lg border border-border bg-background p-4 transition hover:border-gold"
              >
                <p className="font-medium text-navy">{p.h1}</p>
                <p className="mt-1 text-sm text-muted-foreground">{p.turnaround}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices
        exclude={["/services"]}
        limit={3}
        title="Related pages"
        description="More on our scope of work, training and practical resources."
      />
    </>
  );
}
